import { useState, useEffect } from 'react';
import { adminAPI } from '../../lib/api';
import AdminLayout from '../../components/AdminLayout';
import { Input } from '../../components/ui/input';
import { Button } from '../../components/ui/button';
import { Badge } from '../../components/ui/badge';
import { Search, ChevronLeft, ChevronRight, Shield, Edit2, Save, X } from 'lucide-react';
import { toast } from 'sonner';

export default function AdminUsers() {
  const [users, setUsers] = useState([]);
  const [total, setTotal] = useState(0);
  const [search, setSearch] = useState('');
  const [page, setPage] = useState(0);
  const [loading, setLoading] = useState(true);
  const [editing, setEditing] = useState(null);
  const [editData, setEditData] = useState({});
  const limit = 20;

  const loadUsers = async () => {
    setLoading(true);
    try {
      const res = await adminAPI.users(page * limit, limit, search);
      setUsers(res.data.users);
      setTotal(res.data.total);
    } catch {} finally { setLoading(false); }
  };

  useEffect(() => { loadUsers(); }, [page, search]); // eslint-disable-line react-hooks/exhaustive-deps

  const handleSave = async (userId) => {
    try {
      const update = {};
      if (editData.credits !== undefined) update.credits = parseFloat(editData.credits);
      if (editData.plan) update.plan = editData.plan;
      if (editData.is_admin !== undefined) update.is_admin = editData.is_admin;
      await adminAPI.updateUser(userId, update);
      toast.success('User updated');
      setEditing(null);
      loadUsers();
    } catch { toast.error('Update failed'); }
  };

  return (
    <AdminLayout>
      <div data-testid="admin-users-page">
        <div className="flex items-center justify-between mb-8">
          <div>
            <p className="font-mono text-xs text-[#ff003c] tracking-widest uppercase mb-2">USER MANAGEMENT</p>
            <h1 className="font-heading text-3xl font-bold text-white tracking-tight">Users ({total})</h1>
          </div>
        </div>

        {/* Search */}
        <div className="relative max-w-md mb-6">
          <Search className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-[#aaa]" />
          <Input
            placeholder="Search by email or name..."
            value={search}
            onChange={(e) => { setSearch(e.target.value); setPage(0); }}
            className="pl-10 bg-black border-white/10 focus:border-[#FF7A3D] font-mono text-sm placeholder:text-[#555]"
            data-testid="admin-search-users"
          />
        </div>

        {/* Table */}
        <div className="card-cyber rounded-sm overflow-hidden" data-testid="users-table">
          <div className="overflow-x-auto">
            <table className="w-full">
              <thead>
                <tr className="border-b border-white/5">
                  <th className="text-left font-mono text-[10px] text-[#aaa] tracking-widest p-3">USER</th>
                  <th className="text-left font-mono text-[10px] text-[#aaa] tracking-widest p-3">PLAN</th>
                  <th className="text-left font-mono text-[10px] text-[#aaa] tracking-widest p-3">CREDITS</th>
                  <th className="text-left font-mono text-[10px] text-[#aaa] tracking-widest p-3">RUNS</th>
                  <th className="text-left font-mono text-[10px] text-[#aaa] tracking-widest p-3">ROLE</th>
                  <th className="text-left font-mono text-[10px] text-[#aaa] tracking-widest p-3">AUTH</th>
                  <th className="text-right font-mono text-[10px] text-[#aaa] tracking-widest p-3">ACTIONS</th>
                </tr>
              </thead>
              <tbody>
                {users.map(u => (
                  <tr key={u.user_id} className="border-b border-white/[0.03] hover:bg-white/[0.02] transition-colors" data-testid={`user-row-${u.user_id}`}>
                    <td className="p-3">
                      <p className="font-mono text-xs text-white">{u.name || '-'}</p>
                      <p className="font-mono text-[10px] text-[#aaa]">{u.email}</p>
                    </td>
                    <td className="p-3">
                      {editing === u.user_id ? (
                        <select value={editData.plan || u.plan} onChange={(e) => setEditData({...editData, plan: e.target.value})} className="bg-black border border-white/10 text-white font-mono text-xs p-1 rounded-sm">
                          <option value="free">FREE</option>
                          <option value="starter">STARTER</option>
                          <option value="professional">PRO</option>
                          <option value="enterprise">ENTERPRISE</option>
                        </select>
                      ) : (
                        <Badge className="bg-[#FF7A3D]/10 text-[#FF7A3D] border-[#FF7A3D]/20 font-mono text-[10px] uppercase">{u.plan || 'free'}</Badge>
                      )}
                    </td>
                    <td className="p-3">
                      {editing === u.user_id ? (
                        <Input type="number" step="0.01" value={editData.credits ?? u.credits} onChange={(e) => setEditData({...editData, credits: e.target.value})} className="w-24 h-7 bg-black border-white/10 font-mono text-xs" />
                      ) : (
                        <span className="font-mono text-xs text-[#FF7A3D]">{(u.credits || 0).toFixed(2)}</span>
                      )}
                    </td>
                    <td className="p-3"><span className="font-mono text-xs text-[#888]">{u.exec_count || 0}</span></td>
                    <td className="p-3">
                      {u.is_admin ? (
                        <Badge className="bg-[#ff003c]/10 text-[#ff003c] border-[#ff003c]/20 font-mono text-[10px]">ADMIN</Badge>
                      ) : (
                        <span className="font-mono text-[10px] text-[#aaa]">USER</span>
                      )}
                    </td>
                    <td className="p-3"><span className="font-mono text-[10px] text-[#aaa] uppercase">{u.auth_type || '-'}</span></td>
                    <td className="p-3 text-right">
                      {editing === u.user_id ? (
                        <div className="flex items-center gap-1 justify-end">
                          <button onClick={() => handleSave(u.user_id)} className="text-[#FF7A3D] hover:text-white" data-testid={`save-user-${u.user_id}`}>
                            <Save className="w-4 h-4" />
                          </button>
                          <button onClick={() => setEditing(null)} className="text-[#ff003c] hover:text-white">
                            <X className="w-4 h-4" />
                          </button>
                        </div>
                      ) : (
                        <button onClick={() => { setEditing(u.user_id); setEditData({ credits: u.credits, plan: u.plan }); }} className="text-[#aaa] hover:text-[#FF7A3D]" data-testid={`edit-user-${u.user_id}`}>
                          <Edit2 className="w-4 h-4" />
                        </button>
                      )}
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
          {/* Pagination */}
          <div className="flex items-center justify-between p-3 border-t border-white/5">
            <span className="font-mono text-[10px] text-[#aaa]">Page {page + 1} of {Math.max(1, Math.ceil(total / limit))}</span>
            <div className="flex gap-2">
              <button onClick={() => setPage(Math.max(0, page - 1))} disabled={page === 0} className="text-[#aaa] hover:text-white disabled:opacity-30" data-testid="prev-page">
                <ChevronLeft className="w-4 h-4" />
              </button>
              <button onClick={() => setPage(page + 1)} disabled={(page + 1) * limit >= total} className="text-[#aaa] hover:text-white disabled:opacity-30" data-testid="next-page">
                <ChevronRight className="w-4 h-4" />
              </button>
            </div>
          </div>
        </div>
      </div>
    </AdminLayout>
  );
}
